/**
 * Nested saved-view CARD rendering (#1144), styled per mockup bill-table-1c —
 * the card-view counterpart to NestedBillTable, behind the existing
 * table/card ViewToggle. Cell rendering is shared via nestedBillCells so the
 * two renderings cannot drift.
 *
 * Each bill is a card: number pill + subject chips + title, sponsors block,
 * status pill right — with the bill's workspace records as a mini-table
 * inside the card (its label band DOES repeat per card here; a card is a
 * self-contained unit, unlike the table's shared header stack).
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { Bill } from '../../types/bill';
import type { SavedViewColumnEntry, BillWorkspaceRecord } from '../../types/savedViews';
import { subRecordGridTemplate } from './billSubRecordRows';
import {
  billValueCell,
  workspaceValueCell,
  workspaceColumnLabel,
  workspaceColumnDescription,
  BillStatusCell,
  SubjectChips,
  SponsorNames,
  WorkspaceName,
  NestedViewToolbar,
  type WorkspaceColumnLabels,
  type RecordEditing,
  type BillActionsConfig,
} from './nestedBillCells';
import { BILL_COLUMN_CATALOG } from './billColumnCatalog';
import { useProgressiveRender } from '../../hooks/useProgressiveRender';

export interface NestedBillCardsProps {
  bills: Bill[];
  billColumns: SavedViewColumnEntry[];
  workspaceColumns: SavedViewColumnEntry[];
  labels: WorkspaceColumnLabels;
  onOpenNotes?: (bill: Bill, record: BillWorkspaceRecord) => void;
  /** In-place editing of workspace cells (Legislation); read-only when absent. */
  editing?: RecordEditing;
  /** Bill-level Actions column config (when the view selects it). */
  billActions?: BillActionsConfig;
  trackedMode?: boolean;
  onTrackedModeChange?: (tracked: boolean) => void;
}

const CATALOG_LABELS = new Map(BILL_COLUMN_CATALOG.map((c) => [c.colId, c.label]));

/** Bill columns the card header already presents in its own way. */
const CARD_INTRINSIC = new Set(['subjects', 'status', 'sponsors']);

const NestedBillCards: React.FC<NestedBillCardsProps> = ({
  bills,
  billColumns,
  workspaceColumns,
  labels,
  onOpenNotes,
  editing,
  billActions,
  trackedMode,
  onTrackedModeChange,
}) => {
  const navigate = useNavigate();

  const visibleWsCols = workspaceColumns.filter((c) => !c.hide);
  const wsTemplate = `220px ${subRecordGridTemplate(visibleWsCols)}`;
  // Remaining selected bill columns render as a small facts strip under the title.
  const extraBillCols = billColumns.filter((c) => !c.hide && !CARD_INTRINSIC.has(c.colId));
  const showSponsors = billColumns.some((c) => !c.hide && c.colId === 'sponsors');
  const showSubjects = billColumns.some((c) => !c.hide && c.colId === 'subjects');
  const showStatus = billColumns.some((c) => !c.hide && c.colId === 'status');


  const { visibleCount, sentinelRef, hasMore } = useProgressiveRender(bills.length, bills);

  return (
    <div className="space-y-3.5">
      <div className="card p-0 overflow-hidden">
        <NestedViewToolbar
          trackedMode={trackedMode}
          onTrackedModeChange={onTrackedModeChange}
        />
      </div>

      {bills.slice(0, visibleCount).map((bill) => {
        const records = [...(bill.billWorkspaceRecords ?? [])].sort((a, b) =>
          a.workspaceName.toLowerCase().localeCompare(b.workspaceName.toLowerCase())
        );

        return (
          <article
            key={bill.id}
            className="card p-0 overflow-hidden transition-shadow hover:shadow-md"
          >
            {/* Bill header */}
            <header className="grid gap-6 px-5 pt-4 pb-4 md:grid-cols-[1fr,280px,180px] items-start">
              <div className="min-w-0 flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(`/legislation/${bill.id}`)}
                    className="rounded-md bg-primary-800 px-2 py-0.5 text-xs font-bold tracking-wide text-white tabular-nums hover:bg-primary-700"
                  >
                    {bill.billNumber}
                  </button>
                  {showSubjects && <SubjectChips bill={bill} />}
                </div>
                <h3 className="text-lg font-semibold leading-snug text-text-primary dark:text-text-dark-primary break-words">
                  {bill.title}
                </h3>
                {extraBillCols.length > 0 && (
                  <dl className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-[12px]">
                    {extraBillCols.map((col) => (
                      <div key={col.colId} className="flex items-baseline gap-1.5 min-w-0">
                        <dt className="font-bold uppercase tracking-wider text-[10px] text-text-tertiary dark:text-text-dark-tertiary">
                          {CATALOG_LABELS.get(col.colId) ?? col.colId}
                        </dt>
                        <dd className="m-0 min-w-0 text-text-secondary dark:text-text-dark-secondary">
                          {billValueCell(bill, col.colId, billActions)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
              {showSponsors ? (
                <div className="flex flex-col gap-1 min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-text-tertiary dark:text-text-dark-tertiary">
                    Sponsors
                  </span>
                  <SponsorNames bill={bill} mode="dots" />
                </div>
              ) : (
                <span aria-hidden="true" />
              )}
              {showStatus ? <BillStatusCell bill={bill} alignEnd /> : <span aria-hidden="true" />}
            </header>

            {/* Workspace mini-table */}
            {visibleWsCols.length > 0 && (
              <div className="border-t border-border-light dark:border-border-dark overflow-x-auto">
                {/* min-width keeps ws tracks intact; the card scrolls when zoomed/narrow */}
                <div style={{ minWidth: 40 + 220 + visibleWsCols.reduce((n, c) => n + (c.width ?? 160), 0) + 14 * visibleWsCols.length }}>
                <div
                  className="grid gap-3.5 px-5 py-2 bg-primary-50/70 dark:bg-primary-900/20 text-[10px] font-bold uppercase tracking-[0.12em] text-primary-900/60 dark:text-primary-100/60"
                  style={{ gridTemplateColumns: wsTemplate }}
                >
                  <span>Workspace</span>
                  {visibleWsCols.map((col) => {
                    const description = workspaceColumnDescription(col.colId, labels);
                    return (
                      <span
                        key={col.colId}
                        title={description}
                        className={description ? 'cursor-help underline decoration-dotted underline-offset-2' : undefined}
                      >
                        {workspaceColumnLabel(col.colId, labels)}
                      </span>
                    );
                  })}
                </div>
                {records.length === 0 ? (
                  <div className="px-5 py-3 text-[13px] italic text-text-tertiary dark:text-text-dark-tertiary">
                    Not tracked in this view&apos;s workspaces
                  </div>
                ) : (
                  records.map((record) => (
                    <div
                      key={`${bill.id}::${record.workspaceId}`}
                      className="grid gap-3.5 items-center px-5 py-2.5 border-t border-border-light/60 dark:border-border-dark/50 hover:bg-primary-50/50 dark:hover:bg-background-dark-tertiary/40"
                      style={{ gridTemplateColumns: wsTemplate }}
                    >
                      <WorkspaceName name={record.workspaceName} />
                      {visibleWsCols.map((col) => (
                        <span key={col.colId} className="min-w-0">
                          {workspaceValueCell(bill, record, col, onOpenNotes, editing)}
                        </span>
                      ))}
                    </div>
                  ))
                )}
                </div>
              </div>
            )}
          </article>
        );
      })}

      {hasMore && (
        <div ref={sentinelRef} className="card px-5 py-4 text-center text-xs text-text-tertiary dark:text-text-dark-tertiary">
          Loading more bills… ({visibleCount} of {bills.length})
        </div>
      )}

      {bills.length === 0 && (
        <div className="card px-5 py-10 text-center text-sm text-text-tertiary dark:text-text-dark-tertiary">
          No bills found
        </div>
      )}
    </div>
  );
};

export default NestedBillCards;
